<template>
  <Dialog show-close @close="handleClose" :align-bottom="mobile" v-show="!isImageUploadOpen">
    <template v-slot:header>
      <H3>Edit profile</H3>
    </template>
    <div class="hero-bio-update-dialog__content">
      <div class="hero-bio-update-dialog__content-left">
        <img v-if="filePreviewUrl" :src="filePreviewUrl" />
        <img v-else-if="avatar" :src="avatar" />
        <Button color="green" invert @click="isImageUploadOpen = true">Change profile photo</Button>
      </div>
      <div class="hero-bio-update-dialog__content-right">
        <label for="name">Tourhero</label>
        <input class="form-input" type="text" placeholder="Name" v-model="name" />
        <label for="bio">Bio</label>
        <AutosizeTextarea class="form-input" type="text" placeholder="Bio" v-model="bio" :max="1200" />

        <label for="instagramUsername">Social</label>
        <div class="social-input-container">
          <div class="input-with-icon">
            <i class="icon icon-instagram"></i>
            <input class="form-input" type="text" placeholder="Instagram username" v-model="instagramUsername" />
          </div>
          <div class="input-with-icon">
            <i class="icon icon-tiktok"></i>
            <input class="form-input" type="text" placeholder="Tiktok username" v-model="tiktokUsername" />
          </div>
        </div>
      </div>
    </div>
    <div class="hero-bio-update-dialog__save-to-user-checkbox" @click="saveToUser = !saveToUser">
      <SquareCheck class="square-check" :checked="saveToUser" />
      <span>Save this profile to my user</span>
    </div>
    <template v-slot:footer>
      <span v-if="error" class="error-message">{{ error }}</span>
      <div class="hero-bio-update-dialog__footer">
        <Button color="green" invert @click="handleClose" :disabled="isLoading">Cancel</Button>
        <Button color="blue" @click="handleSaveProfile" :disabled="isLoading">Save changes</Button>
      </div>
    </template>
  </Dialog>
  <ImageUploadDialog
    v-if="isImageUploadOpen"
    title="Change profile photo"
    confirmButtonText="Change profile photo"
    cancelButtonText="Cancel"
    :isImageUploadOpen="isImageUploadOpen"
    @close="isImageUploadOpen = false"
    @save="onFileSubmitted"
    close-after-save
    :loading="isLoading"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import _ from 'lodash'
import { Dialog, H3 } from '@/components/Theme'
import Button from '@/components/Theme2/Button.vue'
import AutosizeTextarea from '../../LiteCMS/Common/AutosizeTextarea.vue'
import ImageUploadDialog from './ImageUploadDialog.vue'
import { useScrollLock } from '@vueuse/core'
import { useResponsive } from '@/helpers/vueHelpers.js'
import SquareCheck from '@/components/Theme/SquareCheck.vue'
import { useTypedItineraryStore } from 'app/javascript/src/stores/typed'
import { Product } from 'app/javascript/src/models'
import { UpdateProductRequestDto } from 'app/javascript/src/types'

const props = defineProps<{
  productId: string
  avatar?: string
  user: {
    name?: string
    bio?: string
    instagramUsername?: string
    tiktokUsername?: string
  }
}>()

const { mobile } = useResponsive()
const locked = useScrollLock(document)
const store = useTypedItineraryStore<Product>()

const name = ref(props.user.name ?? '')
const bio = ref(props.user.bio ?? '')
const instagramUsername = ref(props.user.instagramUsername ?? '')
const tiktokUsername = ref(props.user.tiktokUsername ?? '')
const isImageUploadOpen = ref(false)
const saveToUser = ref(false)
const files = ref<File[]>([])
const isLoading = ref(false)
const error = ref<string | null>(null)

const filePreviewUrl = computed(() => {
  return files.value.length > 0 ? URL.createObjectURL(files.value[0]) : null
})

onMounted(() => {
  locked.value = true
})

onUnmounted(() => {
  locked.value = false

  if (filePreviewUrl.value) {
    URL.revokeObjectURL(filePreviewUrl.value)
  }
})

watch(
  [
    () => name.value,
    () => bio.value,
    () => instagramUsername.value,
    () => tiktokUsername.value,
    () => saveToUser.value,
    () => files.value.length,
  ],
  () => {
    error.value = null
  },
)

const emit = defineEmits<{
  (e: 'close'): void
}>()

const handleSaveProfile = async () => {
  isLoading.value = true
  try {
    const user: UpdateProductRequestDto['user'] = {
      name: name.value,
      description: bio.value,
      instagram_username: instagramUsername.value,
      tiktok_username: tiktokUsername.value,
      photo: files.value[0],
      save_to_user: saveToUser.value,
    }

    await store.updateProduct({
      productId: props.productId,
      user: _.omitBy(user, _.isNil),
    } satisfies UpdateProductRequestDto)
    isLoading.value = false
    handleClose()
  } catch (err) {
    console.debug(err)
    error.value = 'Unable to save changes. Please try again later.'
    isLoading.value = false
  }
}

const handleClose = () => {
  if (isLoading.value) return
  emit('close')
}

const onFileSubmitted = ({ files: _files }: { files: File[] }) => {
  files.value = _files
}
</script>

<style lang="scss" scoped>
.theme-dialog :deep(.modal__container) {
  @media (min-width: $breakpoint-m) {
    max-width: 45vw;
  }

  @media (min-width: $breakpoint-lg) {
    max-width: 55vw;
  }
}

.hero-bio-update-dialog__content {
  gap: $spacing-4;
  margin-bottom: $spacing-6;

  @include desktop {
    display: grid;
    grid-template-columns: 1fr 2fr;
  }

  @include mobile {
    display: flex;
    flex-direction: column;
  }

  img {
    width: 100%;
    border-radius: $spacing-4;
  }

  .hero-bio-update-dialog__content-left {
    @include flex-column($spacing-2);
    align-items: center;

    img {
      height: 250px;
      object-fit: contain;
    }
  }

  .hero-bio-update-dialog__content-right {
    @include flex-column;
    width: 100%;

    label {
      color: $grey-100;
      text-transform: uppercase;
      font-size: $text-sm;
      margin-bottom: $spacing-1;
    }

    input,
    textarea {
      border-radius: $rounded-xl;
      margin-bottom: $spacing-6;
    }

    .social-input-container {
      display: flex;
      gap: $spacing-2;

      @include mobile {
        flex-direction: column;
      }

      .input-with-icon {
        position: relative;
        display: flex;
        align-items: center;

        .icon {
          position: absolute;
          left: $spacing-4;
          z-index: 1;
          font-size: 16px;
        }

        input {
          padding-left: calc($spacing-4 * 2 + 6px);
          margin-bottom: 0;
        }
      }
    }
  }
}

.hero-bio-update-dialog__save-to-user-checkbox {
  display: flex;
  align-items: center;
  gap: $spacing-2;
  cursor: pointer;
  width: 100%;
  padding: $spacing-4;
  border-radius: $rounded-xl;
  border: $border-default-lighter;
}

.error-message {
  display: block;
  text-align: center;
  justify-self: center;
  color: $red-300;
  margin-bottom: $spacing-4;
}

.hero-bio-update-dialog__footer {
  display: flex;
  justify-content: center;
  gap: $spacing-4;
}
</style>
